import type {
  Blog,
  BlogPortableText,
  BlogTileData,
} from '@bulknutrients/bulk-shared-lib/interfaces/Blog';
import type { MetaData } from '@bulknutrients/bulk-shared-lib/interfaces/MetaData';
import type { BaseProduct } from '@bulknutrients/bulk-shared-lib/interfaces/Product';
import type { Recipe } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { calculateHeight } from '@bulknutrients/bulk-shared-lib/lib/images';
import {
  getAllBlogSlugs,
  getBlogData,
  getBlogMetaData,
  getFeaturedProducts,
  getRelatedBlogs,
  getSingleBlogDataById,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/blogData';
import { addBigCommerceDataToBaseProducts } from '@bulknutrients/bulk-shared-lib/lib/sanity/products/transform';
import { getRedirect } from '@bulknutrients/bulk-shared-lib/lib/sanity/redirect';
import {
  getLongDateFromDateString,
  portableTextToPlainText,
} from '@bulknutrients/bulk-shared-lib/utils';
import { Aside } from '@components/Aside/Aside';
import { BlogAuthorBlurb } from '@components/Author/BlogAuthor';
import { BlogCard } from '@components/Blog/BlogCard';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { EmblaCarousel } from '@components/Carousels/Carousel';
import { Container } from '@components/Container';
import { EmailBlogBlock } from '@components/EmailBlog';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts';
import { CustomImage, getImageSrcUrl } from '@components/Media/Image/Image';
import { NewsletterSubscriptionForm } from '@components/NewsletterSubForm/NewsletterSubForm';
import { PortableTextBlockRenderer } from '@components/PortableText/PortableText';
import { ProductCardVerticalVariant } from '@components/ProductCard/ProductCard';
import { SocialShares } from '@components/Social';
import { BannerH2 } from '@components/Typography/Headings/headings';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useLocalStorage } from '@hooks/useLocalStorage';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { type ReactElement, type ReactNode, useEffect } from 'react';

const RendererOverride = {
  block: {
    h2: ({ children }: { children?: ReactNode }) => (
      <>
        <HorizontalDivider />
        <h2 className="italic [&+.hr]:hidden has-[+.hr]:mb-5">{children}</h2>
      </>
    ),
    h3: ({ children }: { children?: ReactNode }) => (
      <>
        <HorizontalDivider className="via-grey-mid" />
        <h3>{children}</h3>
      </>
    ),
  },
};
interface IRecipe {
  '@context': string;
  '@type': string;
  name: string;
  image: string[];
  author: {
    '@type': string;
    name: string;
  };
  datePublished: string;
  description: string;
  prepTime: string;
  cookTime: string;
  totalTime: string;
  recipeYield: number;
  nutrition: {
    '@type': string;
    calories: number;
  };
  recipeIngredient: string[];
  recipeInstructions: {
    '@type': string;
    name: string;
    text: string;
  }[];
}

export default function BlogPage({
  blogData,
  breadcrumbs,
  meta,
  relatedBlogs,
  featuredProducts,
  isPreview,
}: {
  blogData: Blog;
  breadcrumbs: { title: string; path: string }[];
  meta: MetaData;
  relatedBlogs: BlogTileData[];
  featuredProducts: BaseProduct[];
  isPreview: boolean;
}): ReactElement {
  const { setRecentlyViewedBlog } = useLocalStorage();

  useEffect(() => {
    setRecentlyViewedBlog({
      blog: blogData,
      viewedTime: Date.now(),
    });
  }, [blogData, setRecentlyViewedBlog]);

  const recipes = blogData.body.filter((d) => d._type === 'recipe') as Recipe[];

  let recipeStructuredData: unknown;
  if (recipes.length > 0) {
    recipeStructuredData = recipes
      .filter((recipe) => !!recipe.instructionsSection)
      .map((recipe) => ({
        '@context': 'https://schema.org/',
        '@type': 'Recipe',
        name: blogData.title,
        image: [getImageSrcUrl({ image: blogData.featuredImage, width: 500 })],
        author: {
          '@type': 'Person',
          name: blogData.author ? blogData.author.name : '',
        },
        datePublished: blogData.scheduledDatetime,
        description: blogData.summary
          ? blogData.summary
              .filter((b) => Object.hasOwn(b, 'children'))
              .map((b) => b.children.map((c) => c.text).join(''))
              .join('')
          : '',

        // for the next three numbers, start em with a PT and end in an M as per https://en.wikipedia.org/wiki/ISO_8601

        prepTime: `PT${recipe.prepTime}M`,
        cookTime: `PT${recipe.cookTime}M`,
        totalTime: `PT${recipe.totalTime}M`,

        //   PT{{number}}M

        recipeYield: recipe.yield,
        nutrition: {
          '@type': 'NutritionInformation',
          calories: `${recipe.calories} calories`,
        },
        recipeIngredient: recipe.ingredientsSection.flatMap(
          (i) => i.ingredients
        ),
        recipeInstructions: recipe.instructionsSection
          .flatMap((i) => i.instructions)
          .map((instruction) => ({
            '@type': 'HowToStep',
            name: instruction.name,
            text: instruction.description,
          })),
      }));
  } else {
    recipeStructuredData = null;
  }
  // only show Estimated reading time when there is estimatedReadingTime and the blog is not a recipe
  const shouldShowReadingTime =
    blogData.estimatedReadingTime && blogData.category !== 'Recipes';

  return (
    <>
      <Head>
        <title>{meta.metaTitle}</title>
        <meta name="title" content={meta.metaTitle} />
        <meta name="description" content={meta.metaDescription} />
        <meta
          property="og:url"
          content={`https://www.bulknutrients.com.au${blogData.slug}`}
        />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blogData.title} />
        {blogData.summary !== null && (
          <meta
            property="og:description"
            content={portableTextToPlainText(blogData.summary)}
          />
        )}
        <meta
          property="og:image"
          content={
            getImageSrcUrl({
              image: blogData.featuredImage,
              width: 500,
            }) as string
          }
        />
        {meta.noIndex && <meta name="robots" content="noindex" />}
        <link
          rel="canonical"
          href={
            meta.canonical
              ? meta.canonical
              : `${process.env.NEXT_PUBLIC_SERVER_URL}${blogData.slug}`
          }
        />
        {recipeStructuredData
          ? (recipeStructuredData as IRecipe[]).map((recipe) => (
              <script
                key={recipe.name}
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                  __html: JSON.stringify(recipe),
                }}
              />
            ))
          : null}
      </Head>
      <div data-testid="blog">
        <Container>
          <Breadcrumbs crumbs={breadcrumbs} />
        </Container>

        <Container
          as="main"
          id="main-content"
          className="grid-cols-[1fr_min(var(--width-narrow-page),calc(100%-16px))_1fr]"
        >
          <h1>{blogData.title}</h1>
          {blogData.author ? (
            <aside className="mb-4 flex items-center gap-4 w-fit">
              {blogData.author.image && (
                <CustomImage
                  image={
                    blogData.author.slug && blogData.author.profilePage
                      ? {
                          ...blogData.author.image,
                          internalLink: blogData.author.slug,
                        }
                      : blogData.author.image
                  }
                  width={75}
                  height={75}
                  round
                />
              )}
              <Paragraph className="m-0">
                <strong>
                  {blogData.author.slug && blogData.author.profilePage ? (
                    <TextLink href={blogData.author.slug}>
                      {blogData.author.name}
                    </TextLink>
                  ) : (
                    blogData.author.name
                  )}
                </strong>
                {blogData.displayDate ? (
                  <>
                    <br />
                    {getLongDateFromDateString(blogData.scheduledDatetime)}{' '}
                    {blogData.lastUpdated &&
                    blogData.lastUpdated !== blogData.scheduledDatetime ? (
                      <>
                        {'- '}
                        <span className="italic">
                          Updated{' '}
                          {getLongDateFromDateString(blogData.lastUpdated)}
                        </span>
                      </>
                    ) : null}
                  </>
                ) : null}
                {!!shouldShowReadingTime && (
                  <>
                    <br />
                    {blogData.estimatedReadingTime} minute read
                  </>
                )}
              </Paragraph>
            </aside>
          ) : (
            <>no author</>
          )}
          <CustomImage
            image={blogData.featuredImage}
            width={800}
            height={calculateHeight(800, blogData.featuredImage.ratio)}
            priority={true}
            className="min-h-[200px] border border-solid border-grey-mid bg-grey-light p-2.5"
          />
          {blogData.summary !== null && (
            <Aside>
              <PortableTextBlockRenderer
                content={blogData.summary}
                componentsOverride={RendererOverride}
              />
            </Aside>
          )}
          <SocialShares
            type="Bulk Blog"
            shareSlug={blogData.slug}
            titleText={`${blogData.title}`}
            imageSource={blogData.featuredImageUrl}
          />
          <PortableTextBlockRenderer
            content={blogData.body}
            componentsOverride={RendererOverride}
          />
          {blogData.author && blogData.author.name !== 'Bulk Nutrients' && (
            <>
              <HorizontalDivider className="mx-0 my-8" />
              <BlogAuthorBlurb author={blogData.author} />
            </>
          )}
          {blogData.references && blogData.references.length > 0 && (
            <>
              <HorizontalDivider className="mx-0 my-8" />
              <div>
                <BannerH2>References:</BannerH2>
                <PortableTextBlockRenderer
                  content={blogData.references}
                  componentsOverride={RendererOverride}
                />
              </div>
            </>
          )}
          {relatedBlogs.length > 0 && (
            <>
              <HorizontalDivider className="mx-0 my-8" />
              <div className="grid grid-cols-1 gap-4">
                <EmblaCarousel
                  title="Related Blogs"
                  slides={relatedBlogs.map((blog) => (
                    <BlogCard key={blog.slug} blog={blog} />
                  ))}
                />
              </div>
            </>
          )}
          {featuredProducts.length > 0 && (
            <>
              <HorizontalDivider className="mx-0 my-8" />
              <div className="grid grid-cols-1 gap-4">
                <EmblaCarousel
                  title="Featured Products"
                  slides={featuredProducts.map((product) => (
                    <ProductCardVerticalVariant
                      key={product.entityId}
                      product={product}
                    />
                  ))}
                />
              </div>
            </>
          )}
          {blogData.newsletterSubscribeBlurb && (
            <>
              <HorizontalDivider className="mx-0 my-8" />
              <NewsletterSubscriptionForm source="BN - Blog Footer Sign Up" />
            </>
          )}
          {blogData.emailArticleBlurb && (
            <>
              <HorizontalDivider className="mx-0 my-8" />
              <EmailBlogBlock
                href={`mailto:?subject=FW: ${blogData.title}&body=Check out this fantastic blog I just read by Bulk Nutrients:%0d%0a${blogData.title}%0d%0ahttps://www.bulknutrients.com.au${blogData.slug}`}
              />
            </>
          )}
        </Container>
      </div>
    </>
  );
}

BlogPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await getAllBlogSlugs();
  return {
    paths,
    fallback: 'blocking',
  };
};

function isCarousel(item: BlogPortableText | Recipe): item is BlogPortableText {
  return (item as BlogPortableText)._type === 'carousel';
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview,
  previewData,
}) => {
  const blogData = preview
    ? await getSingleBlogDataById((previewData as { id: string }).id)
    : await getBlogData(
        `${params?.category?.toString()}/${params?.blog?.toString()}`
      );

  if (!blogData) {
    // try redirect
    const redirect = await getRedirect(
      `/blog/${params?.category?.toString()}/${params?.blog?.toString()}`
        .split(',')
        .join('/') as string
    );

    if (redirect) {
      return {
        redirect: {
          destination: redirect.newUrl,
          permanent: redirect.permanent,
        },
      };
    }

    // fallback failed
    return {
      notFound: true,
    };
  }

  // Build the full url of the thumbnail image for sharing
  const imgUrl = getImageSrcUrl({ image: blogData.featuredImage, width: 500 });
  blogData.featuredImageUrl = imgUrl;

  const relatedBlogs: BlogTileData[] = blogData.relatedBlogs
    ? await getRelatedBlogs(blogData.relatedBlogs.map((p) => p._ref))
    : [];
  const featuredProducts: BaseProduct[] = blogData.featuredProducts
    ? await getFeaturedProducts(blogData.featuredProducts)
    : [];

  // If there is a product carousel, add the BC data that is needed for a BaseProduct
  // Get all products out
  const products = [] as Array<any>;
  for (const block of blogData.body) {
    if (
      isCarousel(block) &&
      block.items &&
      block.items[0]._type === 'product'
    ) {
      products.push(...block.items);
    }
  }

  // Map products
  const mappedProducts = await addBigCommerceDataToBaseProducts(products);

  // Replace them
  blogData.body = blogData.body.map((block) => {
    if (
      isCarousel(block) &&
      block.items &&
      block.items[0]._type === 'product'
    ) {
      const mappedItems = block.items.map((item) =>
        mappedProducts.find((p) => p.entityId === item.entityId)
      );
      return {
        ...block,
        items: mappedItems,
      };
    }
    return block;
  });

  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'The Bulk Nutrients Blog', path: '/blog' },
    {
      title: blogData.category,
      path: blogData.slug.split('/').slice(0, -1).join('/'),
    },
  ];
  const meta = await getBlogMetaData(blogData._id);

  return {
    props: {
      blogData,
      breadcrumbs,
      meta,
      relatedBlogs,
      featuredProducts,
      isPreview: preview || false,
    },
  };
};
